/* Theme constants for the client-facing portal.
 *
 * Kept in its own module — and the entire `portal/` directory imports
 * nothing from the intranet's `routes/` or `app/` code — so the portal can
 * be lifted out into a standalone Vite/Next app later without a rewrite.
 * Matches the Prosperity Capital Advisors brand: deep navy, teal blue,
 * goldenrod accent, restrained whitespace, serif headings.
 */

export const portalTheme = {
  // Brand palette
  navy:        '#0a2947',
  navyDeep:    '#06192c',
  teal:        '#0069a6',
  gold:        '#d09b2c',
  goldSoft:    '#e6b452',

  // Neutrals — slightly cooler than the intranet's softGray to read more
  // institutional, less office-portal.
  ink:         '#0f1f33',
  inkSubtle:   '#37485a',
  muted:       '#6c7a89',
  hairline:    '#dde2e8',
  hairlineStrong: '#c4cbd4',
  surface:     '#ffffff',
  canvas:      '#f6f7f9',

  // Semantic
  error:       '#9a1c1c',
  success:     '#1f6e4a',

  // Typography stacks. Inter / Playfair Display loaded dynamically when
  // the portal renders so the intranet bundle isn't dragged in.
  fontSerif:   `'Playfair Display', 'Georgia', 'Times New Roman', serif`,
  fontSans:    `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif`,
};

// Google Fonts loader — call from a top-level portal page in a useEffect.
// Idempotent; safe to call from multiple portal pages without dupes.
export function ensurePortalFonts() {
  if (typeof document === 'undefined') return;
  const id = 'portal-google-fonts';
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=Inter:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

// Keyframes the portal needs, injected once. Bootstrap's .spinner-border is
// available globally in the intranet bundle, but `portal/` deliberately
// depends on nothing outside itself so it can be lifted into its own app —
// and a spinner that silently stops spinning after that move is exactly the
// kind of breakage that wouldn't be noticed. Idempotent, same as the fonts.
export function ensurePortalKeyframes() {
  if (typeof document === 'undefined') return;
  const id = 'portal-keyframes';
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = '@keyframes portal-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

// Marketing-style stats shown on the brand panel. Placeholder values —
// swap when wired to real firm data, or move to a small config endpoint
// so they're maintainable without a deploy.
export const PORTAL_HEADLINE_STATS = [
  { value: '$4.7B+',  label: 'in client assets under advisement' },
  { value: '9,200+',  label: 'households served nationwide' },
  { value: '30+',     label: 'years of fiduciary experience' },
  { value: '24/7',    label: 'secure document access' },
];
